import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';

import { Constants } from '@grid/utils/constants';
import {
  anyFnExec,
  CellContext,
  ColorComponent,
  ColorOptions,
  DateCellConfig,
  GridData,
  TimeOptions,
} from '../../../models';
import { FocusManagerService } from '../../../services';
import {
  convertToDateTimeWithoutTimeZoneSuffix,
  toUTCDateTime,
} from '@grid/utils/date-time';

@UntilDestroy()
@Component({
  selector: 'app-date-edit',
  templateUrl: './date-edit.component.html',
  styleUrls: ['./date-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateEditComponent
  extends ColorComponent<GridData>
  implements AfterViewInit, DoCheck
{
  @Input({ required: true }) context!: CellContext<GridData>;

  @ViewChild(MatDatepicker, { static: true }) datepicker!: MatDatepicker<any>;
  @ViewChild('clearBtn') clearBtn!: ElementRef<any>;

  Constants = Constants;
  timeOptions: TimeOptions;

  saveButtonLabel: string;
  clearButtonLabel: string;
  selectedDate: Date;
  selectedTime: string;
  selectedColorId: number;
  minDate?: Date;
  maxDate?: Date;

  constructor(
    private focusManager: FocusManagerService,
    private datePipe: DatePipe,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  get dateConfig(): DateCellConfig {
    return this.context.config as DateCellConfig;
  }

  get contextTimeOptions(): TimeOptions {
    return this.dateConfig.timeOptions;
  }

  get dateValue(): string | Date {
    if (!this.selectedDate) {
      return null;
    }

    if (!this.timeOptions?.allowTimePicker || !this.selectedTime) {
      return this.timeOptions?.withoutTimeZone
        ? convertToDateTimeWithoutTimeZoneSuffix(this.selectedDate)
        : this.selectedDate;
    }

    let selectedDateTime: string | Date;

    this.timeOptions?.withoutTimeZone
      ? (selectedDateTime = convertToDateTimeWithoutTimeZoneSuffix(
          this.selectedDate,
          this.timeValueAsDate.getHours(),
          this.timeValueAsDate.getMinutes()
        ))
      : (selectedDateTime = new Date(
          this.selectedDate.getFullYear(),
          this.selectedDate.getMonth(),
          this.selectedDate.getDate(),
          this.timeValueAsDate.getHours(),
          this.timeValueAsDate.getMinutes()
        ));

    return selectedDateTime;
  }

  get timeValueAsDate(): Date {
    return new Date(`0001-01-01T${this.selectedTime}`);
  }

  get initialColorHex(): number {
    const column = this.context.column.columnDef;

    return this.context.data[this.colorOptions.colorPickerColumnDef][column];
  }

  protected get contextColorOptions(): ColorOptions {
    return this.dateConfig.colorOptions;
  }

  ngDoCheck(): void {
    super.ngDoCheck();

    if (this.context?.config) {
      this.timeOptions = this.contextTimeOptions;
    }
  }

  ngAfterViewInit(): void {
    this.minDate = anyFnExec(this.dateConfig?.options?.minDate) ?? new Date(0);
    this.maxDate = anyFnExec(this.dateConfig?.options?.maxDate);
    this.saveButtonLabel = this.dateConfig?.saveButtonLabel;
    this.clearButtonLabel = this.dateConfig?.clearButtonLabel;

    if (this.context.data[this.context.column.columnDef]) {
      this.selectedDate = this.timeOptions?.withoutTimeZone
        ? toUTCDateTime(this.context.data[this.context.column.columnDef])
        : new Date(this.context.data[this.context.column.columnDef]);
    }

    this.selectedTime = this.datePipe.transform(
      this.selectedDate ?? Date.now().toString(),
      Constants.DefaultValues.timeDisplayFormat
    );

    if (this.dateConfig?.colorOptions) {
      this.selectedColorId = this.initialColorHex;
    }

    this.context.cell.actions?.setDelegateEditState(true);

    setTimeout(() => {
      this.datepicker.openedStream
        .pipe(
          untilDestroyed(this),
          filter(
            () =>
              !this.timeOptions?.allowTimePicker &&
              !this.colorOptions?.allowColorPicker &&
              this.dateConfig?.allowClearOption &&
              this.context.data[this.context.column.columnDef]
          )
        )
        .subscribe(() => this.injectElement(this.clearBtn));

      if (this.dateConfig?.autoOpen !== false) {
        this.datepicker.open();
      }

      this.focusManager.focused
        .pipe(
          untilDestroyed(this),
          filter(
            (element) =>
              element !== this.context.cell.element && !this.datepicker.opened
          )
        )
        .subscribe(() => {
          this.context.cell.actions?.setDelegateEditState(false);
          this.context.cell.actions?.endEdit();
        });
    });
  }

  enterEditMode(): void {
    if (!this.datepicker.opened) {
      this.context.cell.actions?.setDelegateEditState(true);
      this.datepicker.open();
    }
  }

  save(): void {
    this.saveDate();

    if (this.colorOptions?.allowColorPicker) {
      this.saveColor();
    }

    this.context.cell.actions?.setDelegateEditState(false);

    setTimeout(() => {
      this.context.cell.actions.onEdited();
      this.datepicker.close();
    });
  }

  clear(): void {
    this.clearDate();

    if (this.dateConfig?.colorOptions) {
      this.clearColor();
    }

    this.context.cell.actions?.setDelegateEditState(false);

    setTimeout(() => {
      this.context.cell.actions.onEdited();
      this.datepicker.close();
    });
  }

  onTimeChanged(time: string): void {
    if (this.selectedTime === time) {
      return;
    }

    this.selectedTime = time;
  }

  onColorChanged(colorId: number): void {
    if (this.selectedColorId === colorId) {
      return;
    }

    this.selectedColorId = colorId;
  }

  //#region Private methods

  private saveDate(): void {
    this.context.data[this.context.column.columnDef] = this.dateValue;
  }

  private saveColor(): void {
    const column = this.context.column.columnDef;
    const cellColors =
      this.context.data[this.colorOptions.colorPickerColumnDef];

    cellColors[column] = this.selectedColorId;

    this.context.data[this.colorOptions.colorPickerColumnDef] = cellColors;
  }

  private clearDate(): void {
    this.context.data[this.context.column.columnDef] = null;
    this.datepicker.select(null);
  }

  private clearColor(): void {
    const column = this.context.column.columnDef;
    const cellColors =
      this.context.data[this.colorOptions.colorPickerColumnDef];

    cellColors[column] = null;

    this.context.data[this.colorOptions.colorPickerColumnDef] = cellColors;
  }

  private injectElement(element: ElementRef<any>): void {
    const matCalendar = document.getElementsByClassName(
      'mat-datepicker-content'
    )[0] as HTMLElement;

    matCalendar.appendChild(element.nativeElement);

    this.cdr.detectChanges();
  }

  //#endregion
}
